import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { Vessel, VesselSafetyStats } from "../../common/Vessel";
import { VesselEmail } from "../vessel/vesselEmailSlice";
import { staggeredBaseQuery } from "./staggeredBaseQuery";
import { portCreateParams } from "../../common/Vessel";

export const vesselApi = createApi({
  reducerPath: "vesselApi",
  tagTypes: ["Vessel", "VesselSafetyStats"],
  baseQuery: staggeredBaseQuery(""),
  endpoints: (builder) => ({
    getVessels: builder.query<Vessel[], { getAssignees?: boolean }>({
      query: ({ getAssignees }) => {
        return {
          method: "GET",
          url: `vessels${!!getAssignees ? "?getAssignees=true" : ""}`,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Vessel" as const, id })),
              { type: "Vessel", id: "LIST" },
            ]
          : [{ type: "Vessel", id: "LIST" }],
    }),
    getVessel: builder.query<Vessel, String>({
      query: (vesselId) => {
        return {
          method: "GET",
          url: `vessels/${vesselId}`,
        };
      },
    }),
    createVessel: builder.mutation<Vessel, Partial<Vessel>>({
      query: (vessel) => {
        return {
          method: "POST",
          url: "vessels/",
          body: vessel,
        };
      },
      invalidatesTags: [{ type: "Vessel", id: "LIST" }],
    }),
    updateVessel: builder.mutation<
      Vessel,
      { vesselId: string; vessel: Partial<Vessel> }
    >({
      query: ({ vesselId, vessel }) => {
        return {
          method: "PUT",
          url: `vessels/${vesselId}`,
          body: vessel,
        };
      },
      invalidatesTags: (result, error, { vesselId }) => [
        { type: "Vessel", id: vesselId },
      ],
    }),
    deleteVessel: builder.mutation<boolean, String>({
      query: (vesselId) => {
        return {
          method: "DELETE",
          url: `vessels/${vesselId}`,
        };
      },
      invalidatesTags: (result, error, vesselId) => [
        { type: "Vessel", vesselId },
      ],
    }),
    getPorts: builder.query<string[], void>({
      query: () => {
        return {
          method: "GET",
          url: "vessels/ports",
        };
      },
    }),
    createPort: builder.mutation<void, portCreateParams>({
      query({ vesselIds, port, timezone }) {
        return {
          method: "POST",
          url: `vessels/ports?ids=${vesselIds?.join(
            ","
          )}&port=${port}&timezone=${timezone}`,
        };
      },
      invalidatesTags: (result, error, vesselId) => [
        { type: "Vessel", vesselId },
      ],
    }),
    getVesselStats: builder.query<VesselSafetyStats[], {}>({
      query: () => {
        return {
          method: "GET",
          url: "vessels/stats",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "VesselSafetyStats" as const, id })),
              { type: "VesselSafetyStats", id: "LIST" },
            ]
          : [{ type: "VesselSafetyStats", id: "LIST" }],
    }),
    updateVesselStats: builder.mutation<void, VesselSafetyStats[] >({
     query(vesselSafetyStats) {
      return {
        method: "PATCH",
        url: "vessels/stats",
        body: vesselSafetyStats
      }

      },
      invalidatesTags: ["VesselSafetyStats", { type: "VesselSafetyStats", id: "LIST" }],
    }),
    getVesselEmails: builder.query<VesselEmail[], {}>({
      query: () => ({
        method: "GET",
        url: "vessels/emails",
      }),
    }),
  }),
});

export const {
  useGetVesselsQuery,
  useGetVesselQuery,
  useCreateVesselMutation,
  useUpdateVesselMutation,
  useDeleteVesselMutation,
  useGetPortsQuery,
  useCreatePortMutation,
  useGetVesselStatsQuery,
  useUpdateVesselStatsMutation,
  useGetVesselEmailsQuery,
} = vesselApi;
